import { FetchStatus, FieldFilterType, IEntity, IMultipleEntities, PropertyType } from "../../types"
import { IAsyncBoundProperties, IBoundPropertyValue } from "./types"
import { IEntityFilter, IValueExpression } from "../../models"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDataContext, usePageContext } from "../../../design/page/context"

import { Binding } from "../../bindings"
import { BoundProperties } from "./BoundProperties"
import { getProperties } from "../properties"
import { useFormDataContext } from "../../../design/components/forms"

export const useGetPropertyValues = (bindings: Binding[]) => {
    const [properties, setProperties] = useState<IAsyncBoundProperties>({
        status: FetchStatus.Idle
    });

    const { type, result: data, status, error } = useDataContext()
    const { page, pageView } = usePageContext()
    const { values: formValues } = useFormDataContext()

    const filters = useMemo(() => {
        return [...(pageView?.linkFilters || []), ...(pageView?.viewFilters || [])];
    }, [pageView]);

    const fetchData = useCallback((row: IEntity = {}) => {
        try {
            const props = getProperties({ bindings, row, filters });
            const values = bindings.map<IBoundPropertyValue>(p => ({
                id: p.propId,
                type: p.type,
                boundValue: p.value,
                value: props[p.propId]
            }));

            setProperties({ status: FetchStatus.Success, result: new BoundProperties(values) });
        } catch (err: any) {
            setProperties({
                status: FetchStatus.Error,
                error: err.toString(),
            });
        }
    }, [bindings, filters]);

    const isDataRequired = useMemo(() => {
        return hasDataDependency(bindings || [], filters || [])
    }, [bindings, filters]);

    useEffect(() => {
        if (!page || !pageView || !bindings) return;

        setProperties(prevProperties => ({
            ...prevProperties,
            status: FetchStatus.Loading,
            error: undefined
        }));

        if (!isDataRequired) {
            fetchData({});
        } else {
            if (status !== FetchStatus.Success) {
                setProperties(prevProperties => ({
                    ...prevProperties,
                    status,
                    error
                }));
                return;
            }

            const row: IEntity = { ...getRecord(type, data), ...formValues };
            fetchData(row);
        }
        // eslint-disable-next-line
    }, [status, bindings, pageView, formValues])

    return properties
}

const hasFieldFilters = (filters: IEntityFilter[]) => {
    return filters.some(p => p.type === FieldFilterType.Field)
}

function hasDataDependency(bindings: Binding[], filters: IEntityFilter[]): boolean {
    if (hasFieldFilters(filters)) {
        return true;
    }

    return bindings.some(binding => {
        if (binding.type === "Field")
            return true;

        if (binding.type === "Expression") {
            const exprValue = binding.value as IValueExpression;
            return exprValue.parameters.some(param => param.type === "Field");
        }

        if (binding.type === "Const") {
            switch (binding.propType) {
                case PropertyType.PageLink: {
                    const pageLink = binding.value;
                    return pageLink.systemId || hasFieldFilters(pageLink.filters || []);
                }
                case PropertyType.DataLink: {
                    const dataLink = binding.value;
                    return dataLink.systemId || hasFieldFilters(dataLink.filters || []);
                }
                case PropertyType.CalendarOptions: {
                    const options = binding.value;
                    return options.systemId || hasFieldFilters(options.filters || []);
                }
                case PropertyType.GridOptions:
                    return true
            }
        }

        return false;
    });
}

const getRecord = (type: "List" | "Card" | "Static", data?: IEntity | IMultipleEntities) => {
    if (!data)
        return {};

    switch (type) {
        case "List":
            return data.records.length > 0 ? data.records[0] : {};
        case "Card":
            return data;
        case "Static":
            return {};
    }

    return {};
}
